// src/app/modules/consultants/ConsultantManagement.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    fetchConsultantsRequest,
    createConsultantRequest,
    updateConsultantRequest,
    deleteConsultantRequest,
} from "../../../../redux/actions/consultantsActions";

import "../RessourcesStyles/ConsultantManagement.css";

const emptyForm = {
    nomContact: "",
    emailContact: "",
    specialite: "",
    reference: "",
};

const normalize = (v) => String(v || "").trim().toLowerCase();

export default function ConsultantManagement() {
    const dispatch = useDispatch();

    const consultantsState = useSelector((s) => s.consultants || {});
    const consultants = consultantsState.consultants || [];
    const loading = !!consultantsState.loading;

    const [search, setSearch] = useState("");

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        dispatch(fetchConsultantsRequest());
    }, [dispatch]);

    const filtered = useMemo(() => {
        const q = normalize(search);
        return (consultants || []).filter((c) => {
            const n = normalize(c?.nomContact);
            const e = normalize(c?.emailContact);
            const s = normalize(c?.specialite);
            const r = normalize(c?.reference);
            return !q || n.includes(q) || e.includes(q) || s.includes(q) || r.includes(q);
        });
    }, [consultants, search]);

    const openAdd = () => {
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (c) => {
        setIsEdit(true);
        setCurrentId(c.id);
        setForm({
            nomContact: c.nomContact || "",
            emailContact: c.emailContact || "",
            specialite: c.specialite || "",
            reference: c.reference || "",
        });
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setForm(emptyForm);
    };

    const validate = () => {
        if (!form.nomContact?.trim()) return "Nom du contact obligatoire";
        return null;
    };

    const onSubmit = () => {
        const err = validate();
        if (err) {
            Swal.fire({ icon: "warning", text: err, timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            nomContact: form.nomContact.trim(),
            emailContact: form.emailContact?.trim() || null,
            specialite: form.specialite?.trim() || null,
            reference: form.reference?.trim() || null,
        };

        if (isEdit && currentId) {
            dispatch(updateConsultantRequest(currentId, payload));
        } else {
            dispatch(createConsultantRequest(payload));
        }
        closeModal();
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irréversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteConsultantRequest(id));
        });
    };

    return (
        <div className="cnsPage">
            <div className="cnsHeader">
                <div>
                    <h2>Consultants</h2>
                    <div className="cnsSubtitle">Gestion des consultants</div>
                </div>
                <button className="cnsBtn cnsBtnPrimary" onClick={openAdd}>
                    + Ajouter Consultant
                </button>
            </div>

            <div className="cnsTools">
                <input
                    className="cnsSearch"
                    placeholder="Rechercher (nom / email / spécialité / ref)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            <div className="cnsCard">
                <div className="cnsTableWrap">
                    <table className="cnsTable">
                        <thead>
                        <tr>
                            <th>Nom Contact</th>
                            <th>Email</th>
                            <th>Spécialité</th>
                            <th>Référence</th>
                            <th style={{ width: 180 }}>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        {filtered.length === 0 ? (
                            <tr>
                                <td colSpan={5} style={{ textAlign: "center", padding: 18, opacity: 0.7 }}>
                                    Aucun consultant
                                </td>
                            </tr>
                        ) : (
                            filtered.map((c) => (
                                <tr key={c.id}>
                                    <td>{c.nomContact}</td>
                                    <td>{c.emailContact || "-"}</td>
                                    <td>{c.specialite || "-"}</td>
                                    <td>{c.reference || "-"}</td>
                                    <td>
                                        <div className="cnsActions">
                                            <button className="cnsBtn cnsBtnLight" onClick={() => openEdit(c)}>
                                                Modifier
                                            </button>
                                            <button className="cnsBtn cnsBtnDanger" onClick={() => onDelete(c.id)}>
                                                Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>

                    {loading && <div className="cnsLoading">Chargement...</div>}
                </div>
            </div>

            {open && (
                <div className="cnsModal" role="dialog" aria-modal="true" onMouseDown={closeModal}>
                    <div className="cnsModalPanel" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="cnsModalHead">
                            <h3>{isEdit ? "Modifier Consultant" : "Ajouter Consultant"}</h3>
                            <button className="cnsIconBtn" onClick={closeModal}>✕</button>
                        </div>

                        <div className="cnsGrid">
                            <div className="cnsField">
                                <label>Nom Contact *</label>
                                <input value={form.nomContact} onChange={(e) => setForm({ ...form, nomContact: e.target.value })} />
                            </div>

                            <div className="cnsField">
                                <label>Email</label>
                                <input value={form.emailContact} onChange={(e) => setForm({ ...form, emailContact: e.target.value })} />
                            </div>

                            <div className="cnsField">
                                <label>Spécialité</label>
                                <input value={form.specialite} onChange={(e) => setForm({ ...form, specialite: e.target.value })} />
                            </div>

                            <div className="cnsField">
                                <label>Référence</label>
                                <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                            </div>
                        </div>

                        <div className="cnsModalFoot">
                            <button className="cnsBtn cnsBtnLight" onClick={closeModal}>Annuler</button>
                            <button className="cnsBtn cnsBtnPrimary" onClick={onSubmit}>
                                {isEdit ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
